import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Modal, Spin, Steps } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import GeneralInfo from "pages/price-management/Prices/newPrice/tabs/GeneralInfo";
import ProductPrice from "pages/price-management/Prices/newPrice/tabs/ProductPrice";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import { createPrice, getConsumptionBands, getCustomerGroups, updatePrice } from "utils/api";
import { customDateFormatWithoutTime } from "components/Common/formatter";
import { isFieldsForTypeValid } from "pages/price-management/Prices/utils";
import { isEmpty, uniq } from "lodash";
import moment from "moment/moment";
import { USER_TENANT_NAME } from "utils/constants";
import { useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";

const useStyles = makeStyles({
  modal: {
    "& .ant-modal-content .ant-modal-body": {
      padding: "24px 0px !important",
    },
  },
  titleWithDescription: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  container: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    borderLeft: "none",
    borderRight: "none",
    marginBottom: 30,
    paddingBottom: 30,
    padding: "0 50px",
  },
  tabs: {
    "&.ant-tabs > .ant-tabs-nav, .ant-tabs > div > .ant-tabs-nav": {
      padding: "0px!important",
      margin: 0,
    },
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0px 50px",
  },
  stepsContainer: {
    padding: "20px 50px",
    backgroundColor: "#0000000D",
    border: "1px solid #0000000D",
  },
  withMargin: {
    marginRight: 10,
  },
  typeTitle: {
    textTransform: "capitalize",
  },
});

const AVAILABLE_STATUSES = ["Active", "Draft"];

const NewPriceModal = ({ data, step, onClose, update }) => {
  const classes = useStyles();
  const isNew = isEmpty(data);
  const [form] = Form.useForm();
  const [priceData, setPriceData] = useState(null);
  const [isLoading, setLoader] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [current, setCurrent] = useState(step || 0);
  const [disabledNext, setDisabledNext] = useState(true);
  const [consumptionBandList, setConsumptionBandList] = useState([]);
  const [customerGroupList, setCustomerGroupListList] = useState([]);
  const [selectedType, setSelectedType] = useState(null);

  const {
    list: { depot },
  } = useSelector(DictionariesSelector);

  useEffect(() => {
    getConsumptionBands({ pageSize: 1000 }).then(res => {
      setConsumptionBandList(
        res?.data?.result?.items?.filter(item => AVAILABLE_STATUSES.includes(item.status)) || null
      );
    });
    getCustomerGroups({ pageSize: 1000 }).then(res => {
      setCustomerGroupListList(
        res?.data?.result?.items?.filter(item => AVAILABLE_STATUSES.includes(item.status)) || null
      );
    });
  }, []);

  useEffect(() => {
    if (!isNew) {
      form.setFieldsValue({
        ...data,
        activationDate: moment(data?.activationDate),
        deactivationDate: (data?.deactivationDate && moment(data?.deactivationDate)) || null,
      });
      if (data.status === "Active") {
        setCurrent(1);
      }
      setPriceData(data);
      setDisabledNext(false);
      setSelectedType(data.type);
    }
  }, []);

  const depotList = useMemo(() => depot[USER_TENANT_NAME()] && uniq(depot[USER_TENANT_NAME()])?.sort(), []);

  const validateForm = useCallback(() => {
    if (current === 0) {
      if (isFieldsForTypeValid(form.getFieldsValue())) {
        setDisabledNext(false);
      } else {
        setDisabledNext(true);
      }
      return;
    }
    setDisabledNext(false);
  }, [form, current]);

  const tabs = useMemo(
    () => [
      {
        title: "General Information",
        key: "generalInformation",
        children: (
          <GeneralInfo
            consumptionBandList={consumptionBandList}
            customerGroupList={customerGroupList}
            validateForm={validateForm}
            form={form}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            status={data?.status}
            depotList={depotList}
          />
        ),
      },
      {
        title: "Product Price",
        key: "productPrice",
        children: (
          <ProductPrice
            consumptionBandList={consumptionBandList}
            customerGroupList={customerGroupList}
            priceData={priceData}
            type={priceData?.type}
            status={data?.status}
            validateForm={validateForm}
            form={form}
            isNew={isNew}
          />
        ),
      },
    ],
    [form, validateForm, priceData, consumptionBandList, selectedType, data, depotList]
  );

  const openConfirmationModal = useCallback(() => setIsConfirmationOpen(true), []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const onSuccessClose = useCallback(() => {
    update();
    onClose();
  }, []);

  const onNextStep = useCallback(async () => {
    setDisabledNext(true);
    if (current === 0) {
      if (isFieldsForTypeValid(form.getFieldsValue())) {
        setPriceData(form.getFieldsValue());
        setCurrent(1);
      } else {
        form.submit();
      }
    }
  }, [form]);

  const onCreate = useCallback(() => {
    const {
      type,
      name,
      registrationCategory,
      product,
      amount,
      activationDate,
      deactivationDate,
      depots,
      pricingOption,
      consumptionBand,
      customerGroup,
    } = {
      ...priceData,
      ...form.getFieldsValue(),
    };
    if (product && amount && activationDate) {
      setLoader(true);
      const request = isNew ? createPrice : updatePrice;
      const preparedData = {
        type,
        name,
        registrationCategory,
        product,
        amount: Number(amount),
        pricingOption: pricingOption || "Actual",
        activationDate: customDateFormatWithoutTime(activationDate),
        ...(deactivationDate && { deactivationDate: customDateFormatWithoutTime(deactivationDate) }),
        ...(depots && { depots: depots === "ALL DEPOTS" ? depotList : depots }),
        ...(consumptionBand && { consumptionBand }),
        ...(customerGroup && { customerGroup }),
      };
      request(preparedData, data?.id)
        .then(() => {
          setTimeout(() => {
            successMessage(`Price was ${isNew ? "created" : "updated"} successfully`);
            setLoader(false);
            onClose();
            update && update();
          }, 1500);
        })
        .catch(error => {
          setLoader(false);
          if (error.response?.data?.errors[0]?.code === "BASE_PRICE_NOT_FOUND") {
            warnMessage(
              `${error.response?.data?.errors[0]?.desc}, ${customDateFormatWithoutTime(
                error.response?.data?.errors[0]?.items[0]?.activationAt
              )}`,
              5000
            );
            return;
          }
          defaultNgiErrorMethod(error);
        });
    } else {
      form.submit();
    }
  }, [form, priceData, data, isNew, depotList]);

  return (
    <Modal
      title={
        <div className={classes.titleWithDescription}>
          {isNew ? "Create" : "Update"} price
          <span>Please fill all mandatory fields</span>
        </div>
      }
      open
      onCancel={openConfirmationModal}
      closable={false}
      className={classes.modal}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <div className={classes.stepsContainer}>
          <Steps current={current} items={tabs} />
        </div>
        <div className={classes.container}>{tabs[current].children}</div>
        <div className={classes.actions}>
          <CustomButton onClick={openConfirmationModal} type="primary" size="small" color="outlined" text="Cancel" />

          <div>
            {current === 0 && (
              <CustomButton disabled={disabledNext} size="small" text="NEXT STEP" type="primary" onClick={onNextStep} />
            )}
            {current === 1 && (
              <CustomButton
                size="small"
                text={`${isNew ? "CREATE" : "UPDATE"} PRICE`}
                type="primary"
                onClick={onCreate}
              />
            )}
          </div>
        </div>
      </Spin>

      {isConfirmationOpen && (
        <ConfirmationModal
          description="The Price creation is in progress. Are you sure you want to discard the changes?"
          onCancel={closeConfirmationModal}
          onConfirm={onSuccessClose}
        />
      )}
    </Modal>
  );
};

export default NewPriceModal;
